import {
  ChecklistFieldDef,
  ChecklistSectionDef,
  repeatableEntriesKey,
  sectionFlowMode,
} from "../components/DynamicChecklistManagement/DynamicChecklistManagement";
import { checklistValueForMappedSave } from "./checklistDateFormat";
import {
  EC_HIERARCHY_META,
  EC_TAG_TO_ROW_KEY,
  ecBlockGroup,
  ecEntryGroup,
} from "./dynamicChecklistEcVerification";
import { normalizeDynamicTagId } from "./dynamicChecklistScrutinyPrefill";

export type EcVerificationBlockUpdate = {
  ecBlockId: number;
  fields: Record<string, string>;
};

export type EcVerificationEntryUpdate = {
  dateId: number;
  ecBlockId?: number;
  fields: Record<string, string>;
};

function fieldTag(field: ChecklistFieldDef): string {
  return normalizeDynamicTagId(field.scrutinyLinkedDeedId);
}

function collectTaggedFields(
  fields: ChecklistFieldDef[],
  row: Record<string, string>
): Record<string, string> {
  const out: Record<string, string> = {};
  fields.forEach((f) => {
    if (f.disabled) return;
    const tag = fieldTag(f);
    if (!tag || !EC_TAG_TO_ROW_KEY[tag]) return;
    out[tag] = checklistValueForMappedSave(f, row[f.id]);
  });
  return out;
}

/** Build EC block + entry write-back payloads from runtime repeatable values. */
export function buildEcVerificationUpdates(
  sections: ChecklistSectionDef[],
  repeatableValues: Record<string, Record<string, string>[]>
): {
  blocks: EcVerificationBlockUpdate[];
  entries: EcVerificationEntryUpdate[];
} {
  const blocks: EcVerificationBlockUpdate[] = [];
  const entries: EcVerificationEntryUpdate[] = [];

  sections.forEach((section) => {
    if (sectionFlowMode(section) !== "ec_verification") return;

    const blockGroup = ecBlockGroup(section);
    const entryGroup = ecEntryGroup(section);
    if (!blockGroup) return;

    const blockKey = repeatableEntriesKey(section.id, blockGroup.id);
    (repeatableValues[blockKey] || []).forEach((row) => {
      const blockId = parseInt(String(row[EC_HIERARCHY_META.BLOCK_ID] ?? "").trim(), 10);
      if (!Number.isFinite(blockId) || blockId <= 0) return;
      const fields = collectTaggedFields(blockGroup.fields, row);
      if (Object.keys(fields).length) {
        blocks.push({ ecBlockId: blockId, fields });
      }
    });

    if (!entryGroup) return;
    const entryKey = repeatableEntriesKey(section.id, entryGroup.id);
    (repeatableValues[entryKey] || []).forEach((row) => {
      if (row[EC_HIERARCHY_META.RELATED] !== "true") return;
      const dateId = parseInt(String(row[EC_HIERARCHY_META.DATE_ID] ?? "").trim(), 10);
      if (!Number.isFinite(dateId) || dateId <= 0) return;
      const blockIdRaw = String(row[EC_HIERARCHY_META.BLOCK_ID] ?? "").trim();
      const blockIdParsed = blockIdRaw ? parseInt(blockIdRaw, 10) : 0;
      const fields = collectTaggedFields(entryGroup.fields, row);
      if (Object.keys(fields).length) {
        const entryUpdate: EcVerificationEntryUpdate = { dateId, fields };
        if (Number.isFinite(blockIdParsed) && blockIdParsed > 0) {
          entryUpdate.ecBlockId = blockIdParsed;
        }
        entries.push(entryUpdate);
      }
    });
  });

  return { blocks, entries };
}
